import React, { useState } from 'react';
import { Modal } from 'react-bootstrap';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { AiOutlineDownload, AiOutlinePrinter } from 'react-icons/ai';
import { FaUserShield, FaUser } from 'react-icons/fa';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import './UserDetailsModal.css';

export default function UserDetailsModal({ show, onHide, user }) {
  const [showFileNamePrompt, setShowFileNamePrompt] = useState(false);
  const [pendingExportType, setPendingExportType] = useState(null);
  const [customFileName, setCustomFileName] = useState('');

  if (!show || !user) return null;

  const id = user.user_id || user.ID || '';
  const firstName = user.firstName || '';
  const lastName = user.lastName || '';
  const userName = user.userName || '';
  const email = user.email || '';
  const phone = user.phoneNumber || user.Phone || '';
  const dob = user.dob ? new Date(user.dob).toLocaleDateString() : 'N/A';
  const gender = user.gender || '';
  const role = user.role || 'user';

  // File name prompt logic
  const handleExportWithPrompt = (type) => {
    setPendingExportType(type);
    setCustomFileName('');
    setShowFileNamePrompt(true);
  };

  const handleFileNameSubmit = (e) => {
    e.preventDefault();
    let name = customFileName.trim();
    if (!name) name = `user_${id}`;
    if (pendingExportType === 'pdf' && !name.endsWith('.pdf')) name += '.pdf';
    setShowFileNamePrompt(false);
    if (pendingExportType === 'pdf') handleSavePDF(name);
    if (pendingExportType === 'print') handlePrint(name);
  };

  // PDF export
  const handleSavePDF = (customName) => {
    try {
      const doc = new jsPDF({ unit: 'pt', format: 'a4', orientation: 'portrait' });
      const margin = 40;
      const pageWidth = doc.internal.pageSize.getWidth();
      doc.setFontSize(20);
      doc.setTextColor(16, 39, 123);
      doc.text('User Details', margin, margin);
      const rows = [
        ['ID', id],
        ['Name', `${firstName} ${lastName}`],
        ['Username', userName],
        ['Email', email],
        ['Phone', phone],
        ['Date of Birth', dob],
        ['Gender', gender],
        ['Role', role === 'admin' ? 'Admin' : 'User']
      ];
      autoTable(doc, {
        startY: margin + 30,
        margin: { left: margin, right: margin },
        head: [['Field', 'Value']],
        body: rows,
        theme: 'grid',
        styles: { fontSize: 12, cellPadding: 6 },
        headStyles: { fillColor: [16, 39, 123], textColor: 255 },
        columnStyles: { 0: { cellWidth: 120 }, 1: { cellWidth: pageWidth - margin * 2 - 120 } }
      });
      doc.save(customName);
      toast.success('PDF saved successfully!', { autoClose: 2000 });
    } catch (error) {
      toast.error('Error generating PDF. Please try again.', { autoClose: 3000 });
    }
  };

  // Print export
  const handlePrint = () => {
    const printWindow = window.open('', '_blank');
    if (printWindow) {
      printWindow.document.write(`
        <html>
          <head>
            <title>User Details</title>
            <style>
              body { font-family: 'Inter', Arial, sans-serif; margin: 40px; color: #10277B; }
              h2 { color: #10277B; }
              table { width: 100%; border-collapse: collapse; margin-top: 20px; }
              th, td { border: 1px solid #10277B; padding: 10px; text-align: left; font-size: 16px; }
              th { background: #10277B; color: #fff; }
              tr:nth-child(even) { background: #f5f5f5; }
            </style>
          </head>
          <body>
            <h2>User Details</h2>
            <table>
              <tr><th>Field</th><th>Value</th></tr>
              <tr><td>ID</td><td>${id}</td></tr>
              <tr><td>Name</td><td>${firstName} ${lastName}</td></tr>
              <tr><td>Username</td><td>${userName}</td></tr>
              <tr><td>Email</td><td>${email}</td></tr>
              <tr><td>Phone</td><td>${phone}</td></tr>
              <tr><td>Date of Birth</td><td>${dob}</td></tr>
              <tr><td>Gender</td><td>${gender}</td></tr>
              <tr><td>Role</td><td>${role === 'admin' ? 'Admin' : 'User'}</td></tr>
            </table>
          </body>
        </html>
      `);
      printWindow.document.close();
      printWindow.focus();
      printWindow.print();
      printWindow.close();
      toast.success('Print dialog opened!', { autoClose: 2000 });
    } else {
      toast.error('Unable to open print dialog.', { autoClose: 3000 });
    }
  };

  return (
    <Modal show={show} onHide={onHide} size="md" centered backdrop="static">
      <Modal.Header className="modal-header-custom">
        <Modal.Title className="modal-title-custom">User Details</Modal.Title>
        <button type="button" className="btn-close" onClick={onHide} />
      </Modal.Header>
      <div className="modal-actions-row right-actions">
        <AiOutlineDownload className="action-icon" title="Download PDF" onClick={() => handleExportWithPrompt('pdf')} />
        <AiOutlinePrinter className="action-icon" title="Print" onClick={() => handleExportWithPrompt('print')} />
      </div>
      <Modal.Body className="modal-body-custom">
        <div className="customer-details-card">
          <div className="details-row">
            <div className="details-label">ID:</div>
            <div className="details-value">{id}</div>
          </div>
          <div className="details-row">
            <div className="details-label">Name:</div>
            <div className="details-value">{firstName} {lastName}</div>
          </div>
          <div className="details-row">
            <div className="details-label">Username:</div>
            <div className="details-value">{userName}</div>
          </div>
          <div className="details-row">
            <div className="details-label">Email:</div>
            <div className="details-value">{email ? <a href={`mailto:${email}`} className="link">{email}</a> : 'N/A'}</div>
          </div>
          <div className="details-row">
            <div className="details-label">Phone:</div>
            <div className="details-value">{phone ? <a href={`tel:${phone}`} className="link">{phone}</a> : 'N/A'}</div>
          </div>
          <div className="details-row">
            <div className="details-label">Date of Birth:</div>
            <div className="details-value">{dob}</div>
          </div>
          <div className="details-row">
            <div className="details-label">Gender:</div>
            <div className="details-value">{gender}</div>
          </div>
          <div className="details-row">
            <div className="details-label">Role:</div>
            <div className="details-value">
              <span className={`role-badge role-${role}`}>
                {role === 'admin' ? (
                  <><FaUserShield /> Admin</>
                ) : (
                  <><FaUser /> User</>
                )}
              </span>
            </div>
          </div>
        </div>
      </Modal.Body>
      {/* File name prompt modal */}
      {showFileNamePrompt && (
        <div className="file-name-prompt-backdrop">
          <div className="file-name-prompt-modal pretty-modal">
            <form onSubmit={handleFileNameSubmit}>
              <label htmlFor="exportFileName" className="pretty-label">Enter file name:</label>
              <input
                id="exportFileName"
                type="text"
                value={customFileName}
                onChange={e => setCustomFileName(e.target.value)}
                autoFocus
                placeholder={pendingExportType === 'pdf' ? `user_${id}.pdf` : `user_${id}`}
                className="file-name-input pretty-input"
              />
              <div className="file-name-prompt-actions pretty-actions">
                <button type="submit" className="btn-save pretty-save">Save</button>
              </div>
            </form>
          </div>
        </div>
      )}
      <ToastContainer
        position="top-center"
        autoClose={2000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="colored"
      />
    </Modal>
  );
}

